class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class GiftOffer extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.loading = false;
    this.tier1Qty = Number(this.dataset.tier1Qty) || 0;
    this.tier2Qty = Number(this.dataset.tier2Qty) || 0;
    this.upgradeFeeCents = Number(this.dataset.upgradeFeeCents) || 0;

    const source = this.querySelector('[data-gift-units]');
    try {
      this.units = source ? JSON.parse(source.textContent) : [];
    } catch (error) {
      this.units = [];
    }
    if (!Array.isArray(this.units)) this.units = [];

    this.addEventListener('click', (event) => {
      const swatch = event.target.closest('.gift-offer__swatch');
      if (swatch && this.contains(swatch)) {
        event.preventDefault();
        const card = swatch.closest('[data-gift-card]');
        if (card) this.selectSwatch(card, swatch);
        return;
      }

      const addButton = event.target.closest('.gift-offer__add');
      if (addButton && this.contains(addButton)) {
        event.preventDefault();
        const card = addButton.closest('[data-gift-card]');
        if (card) this.addVariant(card);
      }
    });

    this.querySelectorAll('[data-gift-card]').forEach((card) => {
      const selected = card.querySelector('[aria-checked="true"]');
      if (selected) this.renderPrice(card, selected);
    });
  }

  selectSwatch(card, swatch) {
    card.querySelectorAll('.gift-offer__swatch').forEach((button) => {
      button.setAttribute('aria-checked', button === swatch ? 'true' : 'false');
    });

    const image = card.querySelector('.gift-offer__image');
    if (image && swatch.dataset.image) {
      image.src = swatch.dataset.image;
      image.alt = swatch.dataset.imageAlt || '';
    }

    this.renderPrice(card, swatch);
  }

  renderPrice(card, swatch) {
    const priceCents = Number(swatch.dataset.price) || 0;
    const treatment = this.previewTreatment(
      card.dataset.kind,
      priceCents,
      card.dataset.productId,
      Number(swatch.dataset.lineRank) || 999999,
      Number(swatch.dataset.lineQty) || 0
    );
    const finalPrice = card.querySelector('.gift-offer__final');
    const compareWrap = card.querySelector('.gift-offer__compare-wrap');
    const comparePrice = card.querySelector('.gift-offer__compare');
    let text = swatch.dataset.priceFormatted || '';
    let showCompare = false;

    if (treatment === 'free' && priceCents > 0) {
      text = this.dataset.freeLabel || 'Free';
      showCompare = true;
    } else if (treatment === 'upgrade' && priceCents > this.upgradeFeeCents) {
      text = this.dataset.feeFormatted || '';
      showCompare = true;
    }

    if (finalPrice) finalPrice.textContent = text;
    if (comparePrice) comparePrice.textContent = swatch.dataset.priceFormatted || '';
    if (compareWrap) compareWrap.hidden = !showCompare;
  }

  previewTreatment(kind, priceCents, productId, lineRank, lineQty) {
    const units = [];
    this.units.forEach((line) => {
      const quantity = Number(line.quantity) || 0;
      for (let unitIndex = 0; unitIndex < quantity; unitIndex += 1) {
        units.push({
          kind: line.kind,
          price: Number(line.price) || 0,
          productId: line.productId,
          lineRank: Number(line.lineRank) || 0,
          unitIndex
        });
      }
    });

    units.push({
      kind,
      price: priceCents,
      productId,
      lineRank,
      unitIndex: lineQty > 0 ? lineQty : 0,
      preview: true
    });
    units.sort(compareGiftOfferUnits);

    let tier1 = this.tier1Qty;
    let tier2 = this.tier2Qty;
    let treatment = 'none';

    units.forEach((unit) => {
      let assigned = 'none';
      if (tier1 > 0) {
        assigned = 'free';
        tier1 -= 1;
      } else if (tier2 > 0) {
        assigned = unit.kind === 'premium' ? 'upgrade' : 'free';
        tier2 -= 1;
      }
      if (unit.preview) treatment = assigned;
    });

    return treatment;
  }

  addVariant(card) {
    if (this.loading) return;
    const swatch = card.querySelector('[aria-checked="true"]');
    if (!swatch) return;

    this.loading = true;
    this.clearError(card);
    this.querySelectorAll('.gift-offer__add').forEach((button) => {
      button.disabled = true;
    });

    const body = JSON.stringify({
      items: [{ id: Number(swatch.dataset.variantId), quantity: 1 }],
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    });

    fetch(routes.cart_add_url, { ...fetchConfig(), body })
      .then((response) => {
        return response.json().then((parsedState) => {
          return { ok: response.ok, parsedState };
        });
      })
      .then(({ ok, parsedState }) => {
        const fallback = window.cartStrings && window.cartStrings.error;
        if (!ok || parsedState.status) {
          const description = parsedState && typeof parsedState.description === 'string' ? parsedState.description : '';
          const message = parsedState && typeof parsedState.message === 'string' ? parsedState.message : '';
          this.showError(card, description || message || fallback || '');
          return;
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || !parsedState.sections || !parsedState.sections['cart-drawer']) {
          this.showError(card, fallback || '');
          return;
        }

        cartDrawer.renderContents(parsedState);
      })
      .catch(() => {
        this.showError(card, (window.cartStrings && window.cartStrings.error) || '');
      })
      .finally(() => {
        this.loading = false;
        if (!this.isConnected) return;
        this.querySelectorAll('.gift-offer__add').forEach((button) => {
          button.disabled = false;
        });
      });
  }

  showError(card, message) {
    const error = card.querySelector('.gift-offer__error');
    if (error) error.textContent = message || '';
  }

  clearError(card) {
    this.showError(card, '');
  }
}

function compareGiftOfferUnits(left, right) {
  if (right.price !== left.price) return right.price - left.price;
  if (left.productId !== right.productId) {
    return left.productId < right.productId ? -1 : 1;
  }
  if (left.lineRank !== right.lineRank) return left.lineRank - right.lineRank;
  return left.unitIndex - right.unitIndex;
}

if (!customElements.get('gift-offer')) {
  customElements.define('gift-offer', GiftOffer);
}